import _ from 'lodash'
import { useState } from 'react'
import ApiClient from '../../backend/ApiClient'
import { IClaimFilter } from '../../definitions/Claims'
import Helper from '../../helpers/Helper'
import TimeHelper from '../../helpers/TimeHelper'
import ActivityManager from '../../managers/ActivityManager'
import { useAppSelector } from '../../store/Hooks'
import ClaimsTable from '../ClaimsTable'
import SmartBadgeFilter from '../badge_filter/SmartBadgeFilter'
import RouteHeader from '../controls/RouteHeader'
import SmartCard from '../controls/cards/SmartCard'
import KPICard from '../controls/data_visualization/KPICard'
import StaleObserver from '../controls/stale/StaleObserver'
import useQuery, { buildQueryKey } from '../hooks/useQuery'
import Tile from '../layout/Tile'
import RoutingManager from '../routing/Routing'

export default function ClaimsBreakdown() {
    const params = RoutingManager.getParams<{ contractId: number, patientIds?: number[] }>(useAppSelector(s => s.session.routeParams))
    const time = useAppSelector(s => s.app.timeSettings ?? TimeHelper.timeSettings())
    const [filter, setFilter] = useState<IClaimFilter>({
        memberIds: [],
        patientIds: Helper.hasVal(params?.patientIds) ? params?.patientIds.map(x => x.toString()) : [],
        providerIds: [],
        statusIds: []
    })

    const { status: statusClaimsKPI, response: claimsKPI } = useQuery({
        queryKey: buildQueryKey('claimsKPI', params.contractId, TimeHelper.stringify(time), Helper.generateKeyOfObject(filter)),
        queryFn: () => ApiClient.getClaimsKPI(params.contractId, time, filter),
        cache: true
    })

    const isLoading = statusClaimsKPI === 'loading'

    return (
        <Tile.Container>
            <RouteHeader
                primary='Claims Breakdown'
                timeSettings={time}
                subActions={[
                    { label: 'Updated last week', icon: 'clock', onClick: () => ActivityManager.openActivity('claims') }
                ]}
            />
            <Tile.Row flex={0} spacing={3}>
                <SmartCard flex={1}>
                    <KPICard
                        data={Helper.dataframeToKPICard(claimsKPI?.number)}
                        isLoading={isLoading}
                        label='Number of Claims'
                    />
                </SmartCard>
                <SmartCard flex={1}>
                    <KPICard
                        data={Helper.dataframeToKPICard(claimsKPI?.badClaims)}
                        isLoading={isLoading}
                        inverse
                        unit='%'
                        label='Bad Claims'
                    />
                </SmartCard>
                <SmartCard flex={1}>
                    <KPICard
                        data={Helper.dataframeToKPICard(claimsKPI?.totalPayment)}
                        isLoading={isLoading}
                        unit='$'
                        inverse
                        label='Total Payment'
                    />
                </SmartCard>
            </Tile.Row>
            <Tile.Row flex={0} spacing={1} style={{ width: '100%' }}>
                <SmartBadgeFilter
                    name='Claim status'
                    values={filter.statusIds}
                    contractId={params.contractId}
                    onApply={x => setFilter(f => ({ ...f, statusIds: x }))}
                />
                <SmartBadgeFilter
                    name='Members'
                    values={filter.memberIds.map(String)}
                    contractId={params.contractId}
                    onApply={x => setFilter(f => ({ ...f, memberIds: x.map(x => Number(x)) }))}
                />
                <SmartBadgeFilter
                    name='Patients'
                    values={filter.patientIds}
                    contractId={params.contractId}
                    onApply={x => setFilter(f => ({ ...f, patientIds: x }))}
                />
                <SmartBadgeFilter
                    name='Providers'
                    values={filter.providerIds.map(String)}
                    contractId={params.contractId}
                    onApply={x => setFilter(f => ({ ...f, providerIds: x.map(x => Number(x)) }))}
                />
            </Tile.Row>
            <Tile.Row spacing={3} style={{ overflowY: 'auto' }}>
                <SmartCard flex={1} style={{ overflowY: 'auto' }}>
                    <ClaimsTable
                        kpiIsLoading={isLoading}
                        totalClaims={_.sum(claimsKPI?.number?.points?.map(x => x.y)) ?? 0}
                        contractId={params.contractId}
                        filter={filter}
                    />
                </SmartCard>
            </Tile.Row>
            <StaleObserver status={statusClaimsKPI} />
        </Tile.Container>
    )
}
